/**
 * components/AppHeader.jsx
 * -------------------------
 * Sticky top navigation bar — renders only when the user is logged in.
 *
 * Layout:
 *   Left   — "CardStoard" logo link → /
 *   Center — Nav buttons controlled by settings.nav_items (null = show all):
 *              cards, balls (disabled), builds, sets_binders, wax (disabled), packs (disabled)
 *   Right  — Username (→ /account), analytics icon, admin gear, chat bubble (if chatbot enabled),
 *              about (?), logout door
 *
 * Settings integration:
 *   - fetchSettings() reads /settings/ for chatbot_enabled and nav_items.
 *   - Listens to the "settings-changed" custom event so changes in Admin propagate immediately
 *     without a page reload.
 *
 * show(key) — returns true if nav_items is null (show all) or includes the given key.
 */
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ChatPanel from "./ChatPanel";
import api from "../api/api";
import "./AppHeader.css";

export default function AppHeader() {
  const { user, isLoggedIn, logout } = useAuth();
  const navigate = useNavigate();
  const [chatOpen, setChatOpen] = useState(false);
  const [chatbotEnabled, setChatbotEnabled] = useState(false);
  const [imageAiEnabled, setImageAiEnabled] = useState(false);
  const [navItems, setNavItems] = useState(null); // null = show all

  const userTier = user?.subscription_tier ?? "base";
  const TIER_ORDER = { base: 0, rc: 1, sp: 2, elite: 3 };
  const tierAllows = (required) => (TIER_ORDER[userTier] ?? 0) >= (TIER_ORDER[required] ?? 0);
  const TIER_MAP = { gallery: "rc", analytics: "rc", balls: "sp", sets_binders: "sp", wax: "sp", packs: "sp", builds: "elite" };

  const isEnabled = (key) => {
    if (navItems !== null && !navItems.includes(key)) return false;
    const req = TIER_MAP[key];
    if (req && !tierAllows(req)) return false;
    return true;
  };
  const disabledStyle = { opacity: 0.35, pointerEvents: "none", cursor: "default", filter: "grayscale(0.5)" };

  const fetchSettings = () => {
    api.get("/settings/")
      .then(res => {
        setChatbotEnabled(res.data.chatbot_enabled ?? false);
        setImageAiEnabled(res.data.enable_image_ai ?? false);
        setNavItems(res.data.nav_items ?? null);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!isLoggedIn) return;
    fetchSettings();
    window.addEventListener("settings-changed", fetchSettings);
    return () => window.removeEventListener("settings-changed", fetchSettings);
  }, [isLoggedIn]);


  // Kiosk chat controls
  useEffect(() => {
    const open  = () => setChatOpen(true);
    const close = () => setChatOpen(false);
    window.addEventListener("kiosk-open-chat", open);
    window.addEventListener("kiosk-close-chat", close);
    return () => {
      window.removeEventListener("kiosk-open-chat", open);
      window.removeEventListener("kiosk-close-chat", close);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (!isLoggedIn) return null;

  // Prefer username, fallback to email prefix
  const displayName = user?.username || user?.email?.split("@")[0] || "User";

  return (
    <>
    <header className="app-header">
      {/* --- Left: App title + username --- */}
      <div className="app-header-left">
        <Link to="/" className="app-header-title">
          CardStoard
        </Link>
        <Link to="/account" className="user-info-link" title="Account details">
          <span className="user-info">{displayName}</span>
        </Link>
      </div>

      {/* --- Center: Primary navigation --- */}
      <div className="app-header-center">
        {isEnabled("cards") && (
          <Link to="/list-cards">
            <button className="header-btn" title="View your collection">📇 Cards</button>
          </Link>
        )}
        {isEnabled("gallery") && (
          <Link to="/gallery">
            <button className="header-btn" title="Browse card photos">🎴 Gallery</button>
          </Link>
        )}
        {isEnabled("balls") && (
          <button className="header-btn" onClick={() => navigate("/balls")} title="Auto Balls">⚾ Balls</button>
        )}
        {isEnabled("builds") && (
          <Link to="/builds">
            <button className="header-btn" title="Builds">🏗️ Builds</button>
          </Link>
        )}
        {isEnabled("sets_binders") && (
          <Link to="/set-binders">
            <button className="header-btn" title="Sets / Binders">📓 Sets/Binders</button>
          </Link>
        )}
        {isEnabled("wax") && (
          <button className="header-btn" onClick={() => navigate("/wax")} title="Wax Boxes">📦 Wax</button>
        )}
        {isEnabled("packs") && (
          <button className="header-btn" onClick={() => navigate("/packs")} title="Wax Packs">🧧 Packs</button>
        )}
        {imageAiEnabled && (
          <button className="header-btn" onClick={() => navigate("/scan")} title="Scan / Identify Card">
            📷 Scan
          </button>
        )}
        {isEnabled("capture") && (
          <button className="header-btn" onClick={() => navigate("/batch-capture")} title="Batch Photo Capture">
            📸 Capture
          </button>
        )}
      </div>

      {/* --- Right: admin, chat, about, logout --- */}
      <div className="app-header-right">
        {isEnabled("analytics") && (
          <button className="header-icon-btn" onClick={() => navigate("/analytics")} title="Analytics">📊</button>
        )}
        {!user?.is_demo && (
          <button className="header-icon-btn" onClick={() => navigate("/admin")} title="Admin Settings">
            ⚙️
          </button>
        )}
        {chatbotEnabled && (
          <button
            className="header-icon-btn"
            onClick={() => setChatOpen((o) => !o)}
            title="Collection Assistant"
          >
            💬
          </button>
        )}
        <button className="header-icon-btn" onClick={() => navigate("/about")} title="About CardStoard">
          ❓
        </button>
        <button className="header-icon-btn" onClick={handleLogout} title="Logout">
          🚪
        </button>
      </div>

      {chatOpen && <ChatPanel onClose={() => setChatOpen(false)} />}
    </header>
    {user?.is_demo && (
      <div className="demo-banner">
        <span>Demo Mode — Read Only</span>
        <button className="demo-banner-tour" onClick={() => { sessionStorage.removeItem("demo-tour-done"); window.dispatchEvent(new CustomEvent("demo-start-tour")); }}>
          Take a Tour
        </button>
        <button className="demo-banner-signup" onClick={async () => { await logout(); navigate("/register"); }}>
          Sign Up Free
        </button>
        <button className="demo-banner-exit" onClick={handleLogout}>Exit Demo</button>
      </div>
    )}
    </>
  );
}
